import { ErrorMessage, Field, Form, Formik } from "formik";
import { MDBModalBody } from "mdb-react-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Button from "../../../components/element/button";
import Col from "../../../components/element/col";

import Input from "../../../components/element/input";
import Row from "../../../components/element/row";
import FullModal from "../../../components/modal/full.modal";
import AccountModalHeader from "../../../components/common/account.modal.header";

import { handleCatchError } from "../../../helpers/handle.catch.error";
import { showToast } from "../../../helpers/show.toast";
import { closeProfileInfoModal } from "../../../redux/reducer/modal.reducer";
import {
  getProfile,
  updateProfile,
} from "../../../redux/reducer/profile.reducer";

const profileSchema = Yup.object().shape({
  name: Yup.string().trim().required("Full name is required"),
  dob: Yup.date().required("Date of birth is required"),
  gender: Yup.string().oneOf(["male", "female"]).required("Gender is required"),
});

export default function ProfileInfoModal() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isOpenProfileInfoModal } = useSelector((state) => state.modal);
  const { profileInfo, profileUpdateLoading } = useSelector(
    (state) => state.profile,
  );
  const profile = profileInfo?.user || profileInfo || {};
  const initialValues = {
    name: profile?.name || "",
    phone_number: profileInfo?.username || profile?.username || "",
    dob: profile?.dob || "",
    gender: profile?.gender?.toLowerCase() || "",
  };

  const handleClose = () => {
    dispatch(closeProfileInfoModal());
  };

  const handleSubmit = (values, { setFieldError, setSubmitting }) => {
    dispatch(
      updateProfile({
        name: values.name.trim(),
        dob: values.dob,
        gender: values.gender,
      }),
    )
      .unwrap()
      .then(() => {
        localStorage.setItem("name", values.name.trim());
        dispatch(getProfile());
        showToast(
          t("Profile updated"),
          t("Your personal information has been updated successfully."),
          "success",
        );
        handleClose();
      })
      .catch((error) => handleCatchError(error, setFieldError))
      .finally(() => setSubmitting(false));
  };

  return (
    <FullModal
      independent
      show={isOpenProfileInfoModal}
      close={handleClose}
      showCloseButton
      centered
      scrollable
      screenSize="lg"
      className="product-action-modal profile-info-modal"
      dialogClassName="action-modal-dialog profile-info-modal-dialog"
      contentClassName="action-modal-content profile-info-modal-content"
      content={
        <MDBModalBody className="profile-info-modal-body">
          <AccountModalHeader
            title={t("My Profile")}
            subtitle={t("Manage your personal information")}
            icon="akar-icons:person"
          />

          <Formik
            initialValues={initialValues}
            validationSchema={profileSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="profile-info-form">
                <Row className="_row profile-info-form-grid">
                  <Input
                    className="profile-info-field"
                    label={t("Full Name")}
                    name="name"
                    placeholder={t("Enter your full name")}
                    isRequired
                  />
                  <Input
                    className="profile-info-field profile-info-field-disabled"
                    label={t("Phone Number")}
                    name="phone_number"
                    type="tel"
                    disabled
                  />
                  <Input
                    className="profile-info-field"
                    label={t("Date of Birth")}
                    name="dob"
                    type="date"
                    isRequired
                  />
                  <Col className="_col profile-info-select-field">
                    <label className="_label profile-info-field-label">
                      {t("Gender")} <span>*</span>
                    </label>
                    <span className="profile-info-select-wrap">
                      <Field as="select" name="gender">
                        <option value="">{t("Select your gender")}</option>
                        <option value="male">{t("Male")}</option>
                        <option value="female">{t("Female")}</option>
                      </Field>
                    </span>
                    <ErrorMessage
                      name="gender"
                      component="span"
                      className="element _errors"
                    />
                  </Col>
                </Row>
                <Row className="_row profile-info-actions">
                  <Button
                    type="submit"
                    className="_button profile-info-save"
                    disabled={isSubmitting || profileUpdateLoading}
                  >
                    <span className="_label profile-info-action">
                      {t(profileUpdateLoading ? "Saving..." : "Save Changes")}
                    </span>
                  </Button>
                </Row>
              </Form>
            )}
          </Formik>
        </MDBModalBody>
      }
    />
  );
}
